import { useTranslation } from "react-i18next";
import { List, SpinLoading, ErrorBlock, Empty, Toast } from "antd-mobile";
import { RightOutline, ClockCircleOutline, CheckCircleOutline } from "antd-mobile-icons";
import type { CoachingScenarioItem, MyHomeData, MyHomeTaskItem } from "./types";

interface Props {
  myHome: MyHomeData | undefined;
  scenes: CoachingScenarioItem[];
  loading: boolean;
  error: boolean;
  onSelectTask: (task: MyHomeTaskItem) => void;
  onSelectScene: (scene: CoachingScenarioItem) => void;
}

/** Sprint 8: H5 员工陪练主页 — 顶部待完成任务 + 中部自主练习(开关开启时)+ 底部历史任务。 */
export function MyHomePage({ myHome, scenes, loading, error, onSelectTask, onSelectScene }: Props) {
  const { t } = useTranslation("coaching");
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <SpinLoading color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <ErrorBlock
          status="default"
          title={t("wecomHome.loadFailed")}
          description={t("wecomHome.pullToRefresh")}
        />
      </div>
    );
  }

  const active = myHome?.activeTasks ?? [];
  const completed = myHome?.completedTasks ?? [];
  const selfPracticeAllowed = myHome?.selfPracticeAllowed ?? false;

  // 完全空 + 不允许自主 → 友好空状态
  if (active.length === 0 && completed.length === 0 && !selfPracticeAllowed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <Empty
          imageStyle={{ width: 96 }}
          description={
            <div className="text-sm text-muted-foreground">
              {t("wecomHome.emptyNoTask")}
              <br />
              {t("wecomHome.emptyWaitDispatch")}
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-6">
      {/* 待完成任务 */}
      {active.length > 0 && (
        <>
          <div className="px-4 pt-5 pb-2 fx-enter">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-1.5">
              <ClockCircleOutline className="text-warning" />
              {t("wecomHome.tasksTitle", { count: active.length })}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("wecomHome.tasksHint")}</p>
          </div>
          <List>
            {active.map((task, i) => {
              // 2026-05-20 hotfix:防误触 — task.memberStatus=COMPLETED 时按钮置灰,点击仅 toast 提示,
              // 不发 chat/start 请求(2026-05-19 13 次 400 "已完成,请勿重复进入"全是 cache 未刷新点重的)。
              const isCompleted = task.memberStatus === "COMPLETED";
              return (
                <List.Item
                  key={task.taskMemberId}
                  className={`fx-enter ${isCompleted ? "opacity-50" : ""}`}
                  style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                  prefix={
                    <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center text-base text-warning">
                      {isCompleted ? <CheckCircleOutline /> : <ClockCircleOutline />}
                    </div>
                  }
                  description={
                    <div className="text-xs text-muted-foreground">
                      {task.scenarioName ?? "—"}
                      {isCompleted && (
                        <span className="ml-2 text-success">
                          {t("wecomHome.alreadyCompleted", "已完成")}
                        </span>
                      )}
                      {task.deadline && !isCompleted && (
                        <span className="ml-2">
                          {t("wecomHome.deadlinePrefix", {
                            date: task.deadline.replace("T", " ").slice(5, 16),
                          })}
                        </span>
                      )}
                    </div>
                  }
                  arrow={isCompleted ? null : <RightOutline />}
                  onClick={() => {
                    if (isCompleted) {
                      Toast.show({
                        content: t("wecomHome.completedNoRetry", "本次培训您已完成,无需重复进入"),
                        duration: 1500,
                      });
                      return;
                    }
                    onSelectTask(task);
                  }}
                >
                  <span className="text-sm font-medium">{task.taskName}</span>
                </List.Item>
              );
            })}
          </List>
        </>
      )}

      {/* 自主练习入口(仅租户开启时显示) */}
      {selfPracticeAllowed && scenes.length > 0 && (
        <>
          <div className="px-4 pt-6 pb-2">
            <h2 className="text-base font-semibold text-foreground">
              {t("wecomHome.selfPracticeTitle")}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("wecomHome.selfPracticeHint")}
            </p>
          </div>
          <List>
            {scenes.map((scene, i) => (
              <List.Item
                key={scene.id}
                className="fx-enter"
                style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                prefix={
                  <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center text-base">
                    {scene.emoji ?? "🎯"}
                  </div>
                }
                description={
                  scene.description ?? (scene.mode === "qa_drill" ? "知识问答演练" : "情景对话陪练")
                }
                arrow={<RightOutline />}
                onClick={() => onSelectScene(scene)}
              >
                <span className="text-sm font-medium">{scene.title ?? scene.name}</span>
              </List.Item>
            ))}
          </List>
        </>
      )}

      {/* 历史任务(简短列表) */}
      {completed.length > 0 && (
        <>
          <div className="px-4 pt-6 pb-2">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-1.5">
              <CheckCircleOutline className="text-success" />
              {t("wecomHome.historyTitle", { count: completed.length })}
            </h2>
          </div>
          <List>
            {completed.slice(0, 5).map((task, i) => (
              <List.Item
                key={task.taskMemberId}
                className="fx-enter"
                style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                prefix={
                  <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center text-base text-success">
                    <CheckCircleOutline />
                  </div>
                }
                description={
                  <div className="text-xs text-muted-foreground">{task.scenarioName ?? "—"}</div>
                }
                extra={
                  task.score != null ? (
                    <span className="text-sm font-bold text-primary">
                      {task.score}
                      {t("wecomHome.scoreSuffix")}
                    </span>
                  ) : null
                }
              >
                <span className="text-sm">{task.taskName}</span>
              </List.Item>
            ))}
          </List>
        </>
      )}

      {/* 不允许自主且只有历史 → 提示 */}
      {active.length === 0 && !selfPracticeAllowed && completed.length > 0 && (
        <div className="px-4 pt-4">
          <p className="text-xs text-muted-foreground text-center">
            {t("wecomHome.noNewTaskHint")}
          </p>
        </div>
      )}
    </div>
  );
}
